import { Resource } from '@modules/centers/interfaces/resource';
import TableHeader from '@shared/components/tables/table-header/table-header.vue';
import { Component, Watch } from 'vue-property-decorator';
import FullCalendar, { CalendarOptions, EventApi } from '@fullcalendar/vue';

import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGrid from '@fullcalendar/scrollgrid';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import { BookingEvent } from '@bookings/interfaces/booking-event';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import moment from 'moment';
import { set } from 'lodash';
import { Booking } from '@bookings/interfaces/booking';
import VuexFormProvider from '@shared/components/forms/auto-form/vuex-form-provider/vuex-form-provider';
import { Calendar } from '@fullcalendar/core';
import { Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import tippy from 'tippy.js';
import DrawerBookingForm from '@bookings/components/drawer-booking-form/drawer-booking-form.vue';
import DrawerBookingEventForm from '@bookings/components/drawer-booking-event-form/drawer-booking-event-form.vue';
import { ParsedEvent } from '@bookings/interfaces/calendar';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import Switch from '@shared/components/forms/inputs/switch/switch.vue';
import BookingEventRefuseModal from '@bookings/components/booking-event-refuse-modal/booking-event-refuse-modal.vue';
import BookingEventConfirmModal from '@bookings/components/booking-event-confirm-modal/booking-event-confirm-modal.vue';

@Component({
  name: 'bpc-booking-event-calendar-page',
  components: {
    'full-calendar': FullCalendar,
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-table-header': TableHeader,
    'bpc-page-title': BpcPageTitle,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-filter': StdFilter,
    'bpc-drawer-booking-event-form': DrawerBookingEventForm,
    'bpc-drawer-booking-form': DrawerBookingForm,
    'bpc-std-form': StdForm,
    'bpc-switch': Switch,
    'bpc-booking-event-refuse-modal': BookingEventRefuseModal,
    'bpc-booking-event-confirm-modal': BookingEventConfirmModal,
  },
})
export default class BookingEventCalendar extends mixins(AbstractTable) {
  $refs: AbstractTable['$refs'] & {
    eventForm: InstanceType<typeof DrawerBookingEventForm> & {
      saveEntity(): void;
      formModel: any;
      onDeleteMediaFile(): void;
      searchEntity(): void;

      extraData: string | AnyEntity;
      formProvider: VuexFormProvider;
    };
    fcalendar: InstanceType<typeof FullCalendar> & {
      $emit(event): void;
      getApi(): Calendar;
    };
    bookingForm: InstanceType<typeof DrawerBookingForm> & {
      clickOnSaveButton: () => Promise<string>;
      clickOnSearchButton: () => Promise<string>;
      clickOnReplaceButton: () => Promise<string>;
    };
    stdCancelForm: InstanceType<typeof StdForm> & {
      formModel: any;
      formProvider: EntityFormProvider;
    };
  };

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  public entity: Booking = null;

  public resourcesLoaded: boolean = false;
  public resources: Resource[] = [];

  public items: BookingEvent[] = [];
  public entityName = 'booking-event';
  public pluralEntityName = 'booking-events';
  public permissionEntityNameToUse = 'bookings';

  public bookingFormModelChanged: boolean = false;

  get myCustomPageActions() {
    return {
      printCalendar: {
        activated: true,
        type: 'action',
        icon: 'print', //'times',
        variant: 'primary',
        text: 'print',
        show: true,
        event: 'print-calendar',
        selectElements: false,
        buttonClass: !this.resourcesLoaded ? 'disabled' : '',
        disabled: !this.resourcesLoaded,
        tag: 'left',
        permissions: ['read'],
      },
      resourceView: {
        activated: true,
        type: 'action',
        icon: 'calendar', //'times',
        variant: 'info',
        text: 'calendar_resource_view',
        show: true,
        event: 'resource-view',
        selectElements: false,
        tag: 'left',
        permissions: ['read'],
      },
    };
  }

  get pageActions() {
    return {
      ...this.myCustomPageActions,
      ...this.defaultPageActions,
    };
  }

  public customRowActions = {};

  public stdFilterModel = {
    date: null,
    resource_id: null,
    weekday: null,
  };

  public resourcesSelectInfo = [];

  public showEventDrawerSection = false;
  public showEventForm = false;
  public bookingEvent: BookingEvent = null;

  public isSimpleBooking = true;
  public headingDate: string = '';

  public showBookingDrawerSection: boolean = false;
  public showPrintClass = false;

  constructor() {
    super();
  }

  get startLoadingEntities(): boolean {
    return this.$store.getters[`${this.storeName}/loadingEntities`](this.entityName);
  }

  onEventFormClose() {
    this.$refs.fcalendar.getApi().unselect();
    this.showEventForm = false;
    this.showEventDrawerSection = false;
    this.i++;
  }

  onFormClose() {
    this.$refs.fcalendar.getApi().unselect();
    this.showForm = false;
    this.showDrawerSection = false;
    this.showEventDrawerSection = false;
    this.i++;
  }

  public get filterDate(): string {
    return this.stdFilterModel.weekday || moment().format('YYYY-MM-DD');
  }

  public get slotMinTime() {
    return this.$store.getters['centerSettings/centerSettings']?.default_start_hour;
  }

  public get slotMaxTime() {
    return this.$store.getters['centerSettings/centerSettings']?.default_end_hour;
  }

  public get defaultSlotDuration() {
    const slotDuration =
      this.$store.getters['centerSettings/centerSettings']?.default_slot_duration || 30;
    const hours = Math.floor(slotDuration / 60);
    const minutes = slotDuration % 60 < 10 ? `0${slotDuration % 60}` : slotDuration % 60;

    return `${hours}:${minutes}:00`;
  }

  public get slotDuration() {
    return this.$store.getters['centerSettings/centerSettings']?.default_slot_duration || 30;
  }

  public get labelUpdateOrders() {
    return this.$t('bookings.booking_events.update_orders.confirmation_modal.label');
  }

  public get bookingEventStoreName() {
    return this.bookingEvent && this.bookingEvent.id
      ? `bookingEvent_${this.bookingEvent.id}`
      : 'bookingEvent';
  }

  public get bookingStoreName() {
    return `booking_0`;
  }

  public get bookingModel() {
    return this.$store.getters[`${this.bookingStoreName}/model`];
  }

  public get filterResourceId(): number {
    return this.filterModel ? this.filterModel.resource_id : null;
  }

  public events: ParsedEvent[] = [];
  public excludedEvents: ParsedEvent[] = [];
  public backgroundEvents: ParsedEvent[] = [];

  get allEvents(): ParsedEvent[] {
    return this.events.concat(this.excludedEvents, this.backgroundEvents);
  }

  private calendarOptions: CalendarOptions = {
    plugins: [resourceTimeGridPlugin, interactionPlugin, scrollGrid],
    schedulerLicenseKey: process.env.VUE_APP_FULLCALENDAR_LICENCE_KEY,
    headerToolbar: {
      left: '',
      center: '',
      right: '',
    },
    resources: [],
    resourceOrder: 'ordering',
    resourceLabelDidMount: function(info) {
      // let questionMark = document.createElement('div');
      // questionMark.innerHTML = '<i class="fas fa-info-circle"></i>'
      // questionMark.innerHTML = '<span v-b-tooltip.hover title="' + info.resource.extendedProps.name + '">i</span>'

      tippy(info.el, {
        content: info.resource.extendedProps.name,
      });
    },
    initialView: 'timeGridWeek',
    // views: {
    //   resourceTimeGrid: {
    //     type: 'resourceTimeGrid',
    //     duration: { days: 1 },
    //   },
    // },
    initialEvents: 'events', // alternatively, use the `events` setting to fetch from a feed
    initialDate: this.filterDate,
    viewDidMount: this.gotoCurrentDate,
    editable: true,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    weekends: true,
    unselectAuto: false,
    select: this.handleDateSelect,
    selectOverlap: true,
    eventClick: this.handleEventChange,
    eventsSet: this.handleEvents,
    eventDrop: this.dragAndDropEventChange,
    eventResize: this.dragAndDropEventChange,
    eventOverlap: true,
    datesAboveResources: true,
    dayMinWidth: 100,
    allDaySlot: false,
    slotMinTime: this.slotMinTime,
    slotMaxTime: this.slotMaxTime,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false,
      meridiem: false,
    },
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false,
      meridiem: false,
    },
    slotDuration: this.defaultSlotDuration,
    height: 'auto',
    stickyHeaderDates: 'auto',
    nowIndicator: true,
    weekNumberCalculation: 'ISO',
    locale: this.lang,

    // eventChange: this.handleEventChange,
    /* you can update a remote database when these fire:
    eventAdd:
    eventChange:
    eventRemove:
    */
  };

  public currentEvents: EventApi[] = [];
  private dragAndDropEvent: any = null;

  dragAndDropEventChange(e) {
    const hasConflicts = this.hasConflictWithEvents(e.event.start, e.event.end);
    if (hasConflicts) {
      this.$bpcMessage.showErrorMessage({ text: 'bookings.exclusion_error' });
      e.revert();
      this.$refs.fcalendar.$emit('rerender-events');
      return;
    }
    this.bookingEvent = null;
    this.showUpdateOrdersAfterChangeOption = false;
    this.showReplaceConflictsAfterChangeOption = false;
    this.dragAndDropEvent = e;
    this.$bvModal.show('confirmation-update-modal');
    this.$bpcApi
      .get(`${this.$bpcApiRouteHelper.getEntityPath('booking-event', 'raw')}/${e.event.id}`)
      .subscribe(res => {
        this.bookingEvent = res.data;
        const originalBookingEvent = JSON.parse(JSON.stringify(this.bookingEvent)); // Clone it to check changes
        const start = moment(e.event.start);
        const end = moment(e.event.end);
        const resourceId = e.event.getResources()[0] ? e.event.getResources()[0].id : null;
        const articleId = e.event.getResources()[0]
          ? e.event.getResources()[0].extendedProps.article_id
          : null;
        // Update booking event with new data from resource
        this.bookingEvent.start_time = start.format('HH:mm');
        this.bookingEvent.end_time = end.format('HH:mm');
        this.bookingEvent.date = start.format('YYYY-MM-DD');
        this.bookingEvent.resource_id = resourceId;
        this.bookingEvent.article_id = articleId;

        const basePath = this.$bpcApiRouteHelper.getEntityPath('booking-event');

        this.$bpcApi.post(basePath + '/get-price', this.bookingEvent).subscribe(res => {
          this.newPrice = res['event_price'].toFixed(2);
          this.originalPrice = res['original_event_price'].toFixed(2);
          this.newForcedPrice = res['event_forced_price'].toFixed(2);
          this.suggestedUpdatedOrders = res['suggested_updated_orders'];
          this.noRefund = res['no_refund'];

          this.showUpdateOrdersAfterChangeOption = this.checkShowUpdateOrdersAfterChangeOption(
            originalBookingEvent,
            this.bookingEvent,
          );

          this.showReplaceConflictsAfterChangeOption = this.checkShowReplaceConflictsAfterChangeOption(
            originalBookingEvent,
            this.bookingEvent,
          );
        });
      });
  }

  checkShowUpdateOrdersAfterChangeOption(
    bookingEvent: BookingEvent,
    bookingEventUpdated: BookingEvent,
  ): boolean {
    return (
      bookingEvent.resource_id !== bookingEventUpdated.resource_id ||
      bookingEvent.date !== bookingEventUpdated.date ||
      bookingEvent.start_time?.substr(0, 5) !== bookingEventUpdated.start_time?.substr(0, 5) ||
      bookingEvent.end_time?.substr(0, 5) !== bookingEventUpdated.end_time?.substr(0, 5) ||
      bookingEvent.article_id !== bookingEventUpdated.article_id ||
      bookingEvent.price_category_id !== bookingEventUpdated.price_category_id
    );
  }

  checkShowReplaceConflictsAfterChangeOption(
    bookingEvent: BookingEvent,
    bookingEventUpdated: BookingEvent,
  ): boolean {
    return (
      (bookingEvent.resource_id !== bookingEventUpdated.resource_id ||
        bookingEvent.date !== bookingEventUpdated.date ||
        bookingEvent.start_time?.substr(0, 5) !== bookingEventUpdated.start_time?.substr(0, 5) ||
        bookingEvent.end_time?.substr(0, 5) !== bookingEventUpdated.end_time?.substr(0, 5)) &&
      // If the event has been reduced and contained inside we have to avoid asking
      // First case: start equal and finish before
      !(
        bookingEventUpdated.start_time?.substr(0, 5) === bookingEvent.start_time?.substr(0, 5) &&
        bookingEventUpdated.end_time?.substr(0, 5) < bookingEvent.end_time?.substr(0, 5)
      ) &&
      // Second case: start later and finish equal
      !(
        bookingEventUpdated.start_time?.substr(0, 5) > bookingEvent.start_time?.substr(0, 5) &&
        bookingEventUpdated.end_time?.substr(0, 5) === bookingEvent.end_time?.substr(0, 5)
      ) &&
      // Third case: start later and finish before
      !(
        bookingEventUpdated.start_time?.substr(0, 5) > bookingEvent.start_time?.substr(0, 5) &&
        bookingEventUpdated.end_time?.substr(0, 5) < bookingEvent.end_time?.substr(0, 5)
      )
    );
  }

  handleEventChange(e) {
    this.$store.commit(`${this.bookingEventStoreName}/setCurrentAction`, 'edit'); // To indicate that we are not in the search of booking
    this.showForm = false;
    this.showDrawerSection = false;
    this.showEventForm = true;
    this.showEventDrawerSection = true;

    const event = this.items.find(event => event.id === Number(e.event.id));

    if (event) {
      this.bookingEvent = event;
    }

    this.$store
      .dispatch(`${this.bookingEventStoreName}/initializeFormModel`, {
        entity: 'booking-event',
        fetchEntity: this.bookingEvent ? true : false,
        id: this.bookingEvent.id,
        formName: 'default',
      })
      .then(res => {
        res.add(() => {
          setTimeout(() => {
            const start = moment(e.event.start);
            const end = moment(e.event.end);

            this.$store.commit(`${this.bookingEventStoreName}/setModelProp`, {
              prop: 'start_time',
              value: start.format('HH:mm'),
            });

            this.$store.commit(`${this.bookingEventStoreName}/setModelProp`, {
              prop: 'end_time',
              value: end.format('HH:mm'),
            });

            this.$store.commit(`${this.bookingEventStoreName}/setModelProp`, {
              prop: 'date',
              value: start.format('YYYY-MM-DD'),
            });

            const resourceId = e.event.getResources()[0] ? e.event.getResources()[0].id : null;

            if (resourceId) {
              const resource = this.resources.find(resource => resource.id === Number(resourceId));
              this.$store.commit(`${this.bookingEventStoreName}/setModelProp`, {
                prop: 'resource',
                value: resource,
              });

              this.$store.commit(`${this.bookingEventStoreName}/setModelProp`, {
                prop: 'resource_id',
                value: resourceId,
              });
            }

            // this.$store.commit('${this.bookingStoreName}/setModel', model);
          }, 1000);
        });
      });
  }
  handleDateSelect(selectInfo) {
    const hasConflicts = this.hasConflictWithEvents(selectInfo.startStr, selectInfo.endStr);
    if (hasConflicts) {
      this.$bpcMessage.showErrorMessage({ text: 'bookings.exclusion_error' });
      this.$refs.fcalendar.getApi().unselect();
      this.$refs.fcalendar.$emit('rerender-events');
      return;
    }

    this.$store.commit(`${this.bookingStoreName}/setCurrentAction`, 'new'); // To indicate that we are not in the search of booking
    this.$store.commit(`${this.bookingStoreName}/setInitialModel`, {
      fields: this.$store.getters[`${this.bookingStoreName}/fieldsObject`],
      model: {},
    });
    this.showEventDrawerSection = false;
    this.showEventForm = false;
    this.showForm = true;
    this.showDrawer();

    setTimeout(() => {
      const start = moment(selectInfo.startStr);
      const end = moment(selectInfo.endStr);
      const model = this.$store.getters[`${this.bookingStoreName}/model`];

      const resource = this.resources.find(
        resource => resource.id === Number(this.filterResourceId),
      );

      set(model, 'time_range.start_time', start.format('HH:mm'));
      set(model, 'start_date', start.format('YYYY-MM-DD'));
      set(model, 'date_range.start_date', start.format('YYYY-MM-DD'));
      set(model, 'time_range.end_time', end.format('HH:mm'));

      if (resource) {
        set(model, 'resources', [resource]);
        set(model, 'article', resource['article']);
        this.$root.$emit('updateSuggestedArticles', [resource]);
      }

      this.$store.commit(`${this.bookingStoreName}/setModel`, model);
    }, 2000);
  }

  get displayEventsWithContactColor() {
    return this.$store.getters['centerSettings/displayContactColorInBookingCalendar'];
  }

  parseBookingToCalendarEvent(booking: Booking) {
    const color = this.displayEventsWithContactColor
      ? booking.contact?.color
      : booking?.discipline?.color;
    return {
      id: booking.id,
      title: booking.name
        ? booking.name
        : booking.contact && booking.contact['fullname']
        ? booking.contact['fullname']
        : booking.discipline && booking.discipline['name']
        ? booking.discipline['name']
        : '',
      start: this.formatDateTime(booking.start_date, booking.start_time),
      end: this.formatDateTime(booking.start_date, booking.end_time),
      backgroundColor: color,
      borderColor: color,
      resourceId: `${booking.resources[0]['id']}`,
    };
  }

  hasConflictWithEvents(startTime, endTime): boolean {
    const eventStart = moment(startTime);
    const eventEnd = moment(endTime);

    return this.excludedEvents.some(conflictEvent => {
      // Be sure that it is a conflict in the same resource
      if (Number(conflictEvent.resourceId) !== Number(this.filterResourceId)) {
        return false;
      }
      const conflicStart = moment(conflictEvent.start);
      const conflicEnd = moment(conflictEvent.end);

      // Event:     [---]
      // Conflict:    [----]
      if (eventStart.isSameOrBefore(conflicStart) && eventEnd.isAfter(conflicStart)) {
        return true;
      }
      // Event:          [---]
      // Conflict:    [----]
      if (eventStart.isBefore(conflicEnd) && eventEnd.isSameOrAfter(conflicEnd)) {
        return true;
      }
      // Event:        [---]
      // Conflict:   [-------]
      if (eventStart.isSameOrAfter(conflicStart) && eventEnd.isSameOrBefore(conflicEnd)) {
        return true;
      }
      // Event:     [---------]
      // Conflict:    [----]
      if (eventStart.isSameOrBefore(conflicStart) && eventEnd.isSameOrAfter(conflicEnd)) {
        return true;
      }

      // NOT IN CONFLICTS CASES
      // Event:    [----]
      // Conflict:        [----]
      //
      // Event:           [----]
      // Conflict: [----]
      //
      // Event:         [----]
      // Conflict: [----]
      //
      // Event:    [----]
      // Conflict:      [----]
      return false;
    });
  }

  onEventFormSaved() {
    // this.allEvents = [];
    setTimeout(() => {
      this.$refs.provider.getEntities();
    }, 300);
    this.onEventFormClose();
    // this.$refs.provider.getEntities();
  }

  onFormSaved($event: any = null) {
    // this.allEvents = [];
    setTimeout(() => {
      this.$refs.provider.getEntities();
    }, 300);
    this.onFormClose();
    if (this.nextAction === 'pay') {
      const orders = [];
      $event.data?.bookingEvents?.forEach(bookingEvent => {
        bookingEvent.orders.forEach(order => orders.push(order));
      });
      this.ordersAvailableToPosCheckout = orders;
      this.selectedOrdersToPosCheckout = orders;
      this.selectedContactIdToPosCheckout = $event.data.contact_id;
      this.selectedPriceCategoryIdToPosCheckout = $event.data.price_category_id;
      this.selectedInvoiceIdToPosCheckout = this.$store.getters['pos/currentTicketId'];
      this.openExportPosCheckoutModal();
    }
    this.setNextAction('');
  }

  getEntitiesOnce() {
    if (this.startLoadingEntities !== true) {
      this.$refs.provider.getEntities();
    }
  }

  showDrawer() {
    this.$store.commit(`${this.bookingStoreName}/setIsDirty`, false);
    this.showDrawerSection = true;
  }

  handleEvents(events: EventApi[]) {
    this.currentEvents = events;
  }

  customOnPageAction($event: string) {
    switch ($event) {
      case 'resource-view':
        this.$router.push({ name: 'admin-calendar' });
        break;
      case 'print-calendar':
        if ($event === 'print-calendar') {
          this.showPrintClass = true;
          const selectedResourceId = this.$refs.stdFilter.filterModel.resource_id;
          const selectedResource = this.resources.filter(
            resource => resource.id == selectedResourceId,
          );
          if (selectedResource.length) {
            const selectedResourceName = selectedResource[0].name;
            this.headingDate = this.headingDate + ' - ' + selectedResourceName;
          }
          this.$refs.fcalendar.getApi().setOption('nowIndicator', false);
          window.dispatchEvent(new Event('resize'));
          setTimeout(() => {
            window.print();
          }, 500);
        }
        break;
      // case 'resource-view':
      //   this.$router.push({ name: 'admin-calendar' });

      //   break;
      default:
        break;
    }
  }
  // onFormSearch() {
  //   this.savingOrSearchingForm = true;
  //   this.$refs.stdForm.searchEntity();
  //   this.resourcesSelectInfo = [];
  //   this.$bpcStorage.clear('booking-events');
  // }

  getResources() {
    this.resourcesLoaded = false;

    const params = {
      per_page: 500,
      orderBy: 'ordering',
      orderDirection: 'asc',
      columns: ['id', 'center_id', 'name', 'code', 'ordering', 'article_id'],
      relationFields: ['article.id', 'article.name', 'article.code'],
      include: ['article'],
    };

    this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('resource'), {
        params: params,
      })
      .subscribe(res => {
        this.resources = res.data;
        this.$set(this.calendarOptions, 'resources', this.parseResources(this.resources));
        this.resourcesLoaded = true;
      });
  }

  parseResources(resources: Resource[]) {
    return resources.map(resource => {
      return {
        id: `${resource.id}`,
        title: resource.code,
        name: resource.name,
        ordering: resource.ordering,
        article_id: resource.article_id,
        article: resource.article,
      };
    });
  }

  getSlotPricesCategories() {
    const params = this.$refs.provider.queryConfig.params;
    this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('schedule-slot-ranges') + '/ranges-by-week', {
        params,
      })
      .subscribe(res => {
        const slots = res.data.map(slot => {
          return {
            start: this.formatDateTime(slot.date, slot.start_time),
            end: this.formatDateTime(slot.date, slot.end_time),
            backgroundColor: `${slot.color}`,
            borderColor: `${slot.color}`,
            display: 'background',
            className: ['no-bg-of-print'],
          };
        });

        if (this.filterResourceId !== null) {
          this.backgroundEvents = slots;
          this.$set(this.calendarOptions, 'events', this.allEvents);
        } else {
          this.backgroundEvents = [];
          this.$set(this.calendarOptions, 'events', this.allEvents);
        }

        if (this.$refs.fcalendar) {
          this.$refs.fcalendar.$emit('rerender-events');
        }
      });
  }

  // getSlotPricesCategories() {
  //   const params = this.$refs.provider.queryConfig.params;
  //   this.$bpcApi
  //     .get(this.$bpcApiRouteHelper.getEntityPath('schedule-slot-ranges') + '/ranges-by-date', { params })
  //     .subscribe(res => {
  //       const slots = res.data.map(slot => {
  //         return {
  //           start: this.formatDateTime(params.date, slot.start_time),
  //           end: this.formatDateTime(params.date, slot.end_time),
  //           backgroundColor: `${slot.color}`,
  //           borderColor: `${slot.color}`,
  //           display: 'background',
  //           className: ['slot-price'],
  //         };
  //       });
  //       this.allEvents = this.allEvents.concat(slots);
  //       this.$set(this.calendarOptions, 'events', this.allEvents);
  //       if (this.$refs.fcalendar) {
  //         this.$refs.fcalendar.$emit('rerender-events');
  //       }
  //     });
  // }

  getResourceConflicts() {
    const params = this.$refs.provider.queryConfig.params;
    this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('booking-event') + '/resource-conflicts/weekly', {
        params,
      })
      .subscribe(res => {
        const excludedEvents = res.data.map(excludedEvent => {
          return {
            id: excludedEvent.id,
            title: excludedEvent.name,
            start: this.formatDateTime(excludedEvent.date, excludedEvent.start_time),
            end: this.formatDateTime(excludedEvent.date, excludedEvent.end_time),
            backgroundColor: '#cecece',
            borderColor: '#cecece',
            resourceId: `${excludedEvent.resource_id}`,
            className: ['excluded-event', 'no-bg-of-print'],
            display: 'background',
            extendedProps: {
              resource_id: excludedEvent.resource_id,
              excluded: true,
            },
          };
        });
        this.excludedEvents = excludedEvents;
        this.$set(this.calendarOptions, 'events', this.allEvents);
        if (this.$refs.fcalendar) {
          this.$refs.fcalendar.$emit('rerender-events');
        }
      });
  }

  parseBookingEventToCalendarEvent(event: BookingEvent) {
    const color = this.displayEventsWithContactColor
      ? event.contact?.color
      : event?.discipline?.color;
    return {
      id: event.id,
      title: event.name
        ? event.name
        : event.contact && event.contact['fullname']
        ? event.contact['fullname']
        : event.discipline && event.discipline['name']
        ? event.discipline['name']
        : '',
      booking_type: event.booking_type,
      classNames: [
        `booking-type-${event.booking_type}`,
        `booking-status-${event.status}`,
        event.status === 'requested' ? `booking-show-requested-${this.showRequestedBookings}` : '',
        'add-border',
      ],
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: color,
      borderColor: color,
      resourceId: `${event.resource_id}`,
    };
  }

  addEventsToCalendar() {
    this.items = this.$refs.provider ? this.$refs.provider.items : null;
    const events = this.items.map(event => {
      return this.parseBookingEventToCalendarEvent(event);
    });
    this.events = events;
    this.$set(this.calendarOptions, 'events', this.allEvents);
    if (this.$refs.fcalendar) {
      this.$refs.fcalendar.getApi().unselect();
      this.$refs.fcalendar.$emit('rerender-events');
    }
    // this.calendarOptions.eventsSet;
  }

  gotoCurrentDate() {
    if (this.$refs.fcalendar) {
      this.$refs.fcalendar.getApi().gotoDate(this.filterDate);
    }
  }

  formatDateTime(date, time): string {
    return moment(`${date} ${time}`).toISOString();
  }

  hasBookingEvents(): boolean {
    return this.isSimpleBooking
      ? false
      : !!this.$store.getters[`${this.bookingStoreName}/model`].bookingEvents;
  }

  onFormSearched() {
    // this.showSelectResourceModalIfNeccesary();
    // this.$set(this.$refs.stdForm.$refs.formProvider.model, 'action', 'new');
    // this.currentAction = 'search'; // To indicate that we are not in the search of booking
  }

  syncDateFilterModel(model) {
    if (model) {
      this.updateHeadingDate(model.weekday);
      this.$set(this.stdFilterModel, 'weekday', model.weekday || null);
    }
  }

  updateHeadingDate(date) {
    this.headingDate = '';
    if (date) {
      const locale = this.getLang() === 'be' ? 'fr' : this.getLang();
      this.headingDate = moment(date)
        .locale(locale)
        .format('dddd DD/MM/YYYY')
        .toUpperCase();
    }
  }

  // get isSimpleBooking() {
  //   const model = this.$store.getters[`${this.bookingStoreName}/model`];
  //   const repetitive = model.repetitive ? model.repetitive : false;
  //   return repetitive;
  // }

  /* Lifecycle hooks */

  // created () { }
  mounted() {
    this.exportCheckoutModalId = this.generateUUIDv4();
    this.getResources();
    this.$root.$on('bookingEventSaved', event => {
      this.getEntitiesOnce();
    });
    this.$root.$on('bookingSaved', event => {
      this.getEntitiesOnce();
    });
    this.$root.$on('refresh-form-after-add-orders-to-pos', () => {
      this.i++;
    });

    window.addEventListener('afterprint', this.restoreCalendar);
  }

  // updated () { }
  destroyed() {
    this.$root.$off('bookingEventSaved');
    this.$root.$off('bookingSaved');
    this.$root.$off('refresh-form-after-add-orders-to-pos');
    window.removeEventListener('afterprint', this.restoreCalendar);
  }

  restoreCalendar() {
    setTimeout(() => {
      this.headingDate = this.headingDate.split('-')[0];
      this.showPrintClass = false;
      this.$refs.fcalendar.getApi().setOption('nowIndicator', true);
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }

  // updated () { }

  @Watch('filterDate')
  onChangeFilterDate() {
    this.gotoCurrentDate();
  }

  @Watch('startLoadingEntities')
  onStartLoadingEntities() {
    if (this.startLoadingEntities === true) {
      setTimeout(() => {
        this.getResourceConflicts();
        this.getSlotPricesCategories();
      }, 300);

      // this.addTmpEventInEdition(); // TODO activate this to be able to render the event in edition after getEntities
    }
  }

  @Watch('slotMinTime')
  onChangeSlotMinTime() {
    this.$set(this.calendarOptions, 'slotMinTime', this.slotMinTime);
    if (this.$refs.fcalendar) {
      this.$refs.fcalendar.$emit('render');
    }
  }

  @Watch('slotMaxTime')
  onChangeSlotMaxTime() {
    this.$set(this.calendarOptions, 'slotMaxTime', this.slotMaxTime);
    if (this.$refs.fcalendar) {
      this.$refs.fcalendar.$emit('render');
    }
  }

  @Watch('slotDuration')
  onChangeSlotDuration() {
    const hours = Math.floor(this.slotDuration / 60);
    const minutes =
      this.slotDuration % 60 < 10 ? `0${this.slotDuration % 60}` : this.slotDuration % 60;
    const slotDurationFormated = `${hours}:${minutes}:00`;
    this.$set(this.calendarOptions, 'slotDuration', slotDurationFormated);
    if (this.$refs.fcalendar) {
      this.$refs.fcalendar.$emit('render');
    }
  }

  @Watch('filterResourceId')
  onChangeFilterResource() {
    if (
      this.$refs.provider &&
      this.$refs.provider.canLoadEntities &&
      this.$refs.provider.firstLoadEntities
    ) {
      this.getResources();
    }
  }

  /**
   * Functions for booking forms
   */
  onNew() {
    this.entity = null;
    this.$bpcStorage.clear('booking-events');
    this.showForm = true;
    this.isSimpleBooking = true;
    this.showDrawer();

    setTimeout(() => {
      this.$store.commit(`${this.bookingStoreName}/setInitialModel`, {
        fields: this.$store.getters[`${this.bookingStoreName}/fieldsObject`],
        model: {},
      });

      const resource = this.resources.find(
        resource => resource.id === Number(this.filterResourceId),
      );

      const model = this.$store.getters[`${this.bookingStoreName}/model`];

      set(model, 'start_date', this.filterDate);
      set(model, 'date_range.start_date', this.filterDate);

      set(model, 'date_range.start_date', this.filterDate);

      if (resource) {
        set(model, 'resources', [resource]);
        this.$root.$emit('updateSuggestedArticles', [resource]);
      }

      this.$store.commit(`${this.bookingStoreName}/setModel`, model);
    }, 2000);
  }

  getBookingModalTitle() {
    return this.$t('global.modals.create') + ' ' + this.$t('global.models.booking.name');
  }

  // Modal confirmation for update booking events when make drag and drop
  actionOptionSelected: string = null;
  actionOptions: Record<string, any> = [];
  conflictOptionSelected: string = null;
  conflictOptions: Record<string, any> = [];
  orderOptionSelected: string = null;
  orderOptions: Record<string, any> = [];
  showUpdateOrdersAfterChangeOption: boolean = false;
  showReplaceConflictsAfterChangeOption: boolean = false;
  sendEmailOptionSelected: boolean = this.defaultSendBookingEventChangeEmail;
  public selectedRowEntity = null;

  public newPrice = 0;
  public originalPrice = 0;
  public newForcedPrice = 0;
  public suggestedUpdatedOrders = null;
  public noRefund = null;

  get defaultSendBookingEventChangeEmail(): boolean {
    return !!this.$store.getters['centerSettings/centerSettings']
      ?.admin_send_booking_event_change_email;
  }

  get showSendEmailOption(): boolean {
    return (
      this.$store.getters['centerSettings/centerSettings']?.send_booking_event_change_email &&
      this.bookingEvent.status === 'confirmed'
    );
  }

  public get confirmationUpdateModalTitle() {
    return this.$t('global.modals.update-booking-event.multiple.title');
  }

  public get actionOptionsLabel() {
    return this.$t('global.modals.update-booking-event.multiple.action_option.label');
  }

  public get conflictOptionsLabel() {
    return this.$t('global.modals.update-booking-event.multiple.conflicts_case.label');
  }

  public get sendEmailOptionsLabel() {
    return this.$t('global.modals.update-booking-event.send_email.label');
  }

  public get orderOptionsLabel() {
    return this.$t('global.modals.update-booking-event.multiple.orders.label');
  }

  public get confirmButtonText() {
    return this.$t('global.buttons.confirm');
  }

  public get cancelButtonText() {
    return this.$t('global.buttons.cancel');
  }

  public get profileCanChangeOrders() {
    return this.$authorizationService.hasPermission('force_price_update_booking');
  }

  resetConfirmationUpdateModal() {
    this.actionOptions = [
      {
        text: this.$t('global.modals.update-booking-event.multiple.action_option.options.only'),
        value: 'only_this',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.action_option.options.next_same_events',
        ),
        value: 'next_same',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.action_option.options.all_same_events',
        ),
        value: 'all_same',
      },
    ];

    this.actionOptionSelected = 'only_this';

    this.conflictOptions = [
      {
        text: this.$t('global.modals.update-booking-event.multiple.conflicts_case.options.replace'),
        value: 'replace',
      },
      {
        text: this.$t(
          'global.modals.update-booking-event.multiple.conflicts_case.options.do_nothing',
        ),
        value: 'do_nothing',
      },
    ];

    this.conflictOptionSelected = 'replace';

    this.orderOptions = [
      {
        text: this.$t('global.modals.update-booking-event.multiple.orders.options.update'),
        value: 'update',
      },
      {
        text: this.$t('global.modals.update-booking-event.multiple.orders.options.no_update'),
        value: 'no_update',
      },
    ];

    this.orderOptionSelected = 'update';
  }

  onCancelUpdateEventModal() {
    this.dragAndDropEvent.revert();
    this.dragAndDropEvent = null;
  }

  onConfirmUpdateEventModal() {
    // If the options are not available to select, set by default values to avoid the action
    if (!this.showReplaceConflictsAfterChangeOption) {
      this.conflictOptionSelected = 'do_nothing';
    }
    if (!this.showUpdateOrdersAfterChangeOption) {
      this.orderOptionSelected = 'no_update';
    }

    if (this.bookingEvent.own_to_repetitive_booking) {
      this.bookingEvent.action_for_update = this.actionOptionSelected;
      this.bookingEvent.action_for_conflicts = this.conflictOptionSelected;
    }
    this.bookingEvent.action_for_orders = this.orderOptionSelected;
    this.bookingEvent.notify_by_email = this.sendEmailOptionSelected;

    const updateEventObservable: Observable<BPCGenericApiResponse> = this.$bpcApi.put(
      `${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/${this.bookingEvent.id}`,
      this.bookingEvent,
      null,
    );

    updateEventObservable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
        this.$refs.provider.getEntities();
      },
      error => {
        this.$bvModal.hide('confirmation-update-modal');
        this.bookingEvent = null;
        this.$bpcMessage.showErrorMessage({ text: 'global.generic.error_update_action' });
      },
      () => {
        this.bookingEvent = null;
        this.$bvModal.hide('confirmation-update-modal');
      },
    );
  }

  onCancelledBookingEvent() {
    this.onRefresh();
  }

  repetitiveActionOptionSelected: string = null;
  repetitiveActionOptions: Record<string, any> = [
    {
      text: this.$t('global.modals.update-booking-event.multiple.action_option.options.only'),
      value: 'only_this',
    },
    {
      text: this.$t(
        'global.modals.update-booking-event.multiple.action_option.options.next_same_events',
      ),
      value: 'next_same',
    },
    {
      text: this.$t(
        'global.modals.update-booking-event.multiple.action_option.options.all_same_events',
      ),
      value: 'all_same',
    },
    {
      text: this.$t('global.modals.update-booking-event.multiple.action_option.options.all_events'),
      value: 'cancel_all',
    },
  ];

  onActionDelete(item) {
    this.$refs.softDeleteConfirmationModal.onOpenModal(item.item['id']);
    this.showEventDrawerSection = false;
  }

  // End modal confirmation for update booking events when make drag and drop

  /**
   * TODO activate this to be able to render the event in edition after getEntities
   *
   */

  /**
  addTmpEventInEdition() {
    if (this.tmpEventInEdition) {
      this.allEvents.push(this.tmpEventInEdition);
      this.$set(this.calendarOptions, 'events', this.allEvents);
      if (this.$refs.fcalendar) {
        this.$refs.fcalendar.$emit('rerender-events');
      }
    }
  }

  public get tmpEventInEdition() {
    if (this.bookingModel && !this.bookingModelHasDateAndTimeFields) {
      return this.parseBookingToCalendarEvent(this.bookingModel);
    }

    return null;
  }

  public get bookingModelHasDateAndTimeFields(): boolean {
    return (
      this.bookingModel.start_date && this.bookingModel.start_time && this.bookingModel.end_time
    );
  }
   */

  showRequestedBookings: boolean = true;

  @Watch('showRequestedBookings')
  onChangeShowRequestedBookings() {
    this.addEventsToCalendar();
  }

  /* Refuse booking event */
  showRefuseModal: boolean = false;
  eventIdsToRefuse: string[] | number[] = [];
  eventToRefuseOwnsToRepetitive: boolean = false;
  onActionRefuse(item): void {
    this.eventIdsToRefuse = [item.id];
    this.eventToRefuseOwnsToRepetitive = item.booking?.repetitive || false;
    this.showRefuseModal = true;
  }

  onCloseRefuseModal(): void {
    this.showRefuseModal = false;
    // Reset default data
    this.eventIdsToRefuse = [];
    this.eventToRefuseOwnsToRepetitive = false;
  }

  onSuccessRefuse(): void {
    this.i++;
    this.$refs.provider.getEntities();
  }

  /* Confirm booking event */
  showConfirmModal: boolean = false;
  eventIdsToConfirm: string[] | number[] = [];
  eventToConfirmOwnsToRepetitive: boolean = false;
  onActionConfirm(item): void {
    this.eventIdsToConfirm = [item.id];
    this.eventToConfirmOwnsToRepetitive = item.booking?.repetitive || false;
    this.showConfirmModal = true;
  }

  onCloseConfirmModal(): void {
    this.showConfirmModal = false;
    // Reset default data
    this.eventIdsToConfirm = [];
    this.eventToConfirmOwnsToRepetitive = false;
  }

  onSuccessConfirm(): void {
    this.i++;
    this.$refs.provider.getEntities();
  }
}
